import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useContent } from "../lib/useContent";
import { mediaSrc } from "../lib/media";
import AnimatedSignature from "../components/AnimatedSignature";

export default function Home() {
    const { hero } = useContent();
    return (
        <div data-testid="home-page">
            {/* Hero — full viewport painting + centered animated signature, no other elements */}
            <section className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
                <motion.img
                    src={mediaSrc(hero.image)}
                    alt={hero.image?.alt ?? ''}
                    initial={{ scale: 1.05, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="relative h-full flex items-center justify-center px-6">
                    <AnimatedSignature className="w-[80vw] max-w-[1100px] h-auto" />
                </div>
            </section>

            {/* Quiet intro section */}
            <section className="px-6 md:px-12 lg:px-24 py-32 md:py-48">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
                    <div className="md:col-span-3">
                        <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-accent">
                            01 — Studio Notes
                        </p>
                    </div>
                    <div className="md:col-span-9">
                        <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter text-aplum">
                            A practice that lingers in the
                            <span className="italic text-aplum-accent"> in-between </span>
                            — the half-light, the unfinished gesture, the breath before form arrives.
                        </h2>
                        <p className="font-sans-display text-base md:text-lg text-aplum-dim mt-10 max-w-2xl leading-relaxed">
                            Alexandra Plum is a contemporary figurative artist working across drawing,
                            painting and mixed media. Her work has been exhibited internationally and
                            held in private collections across Europe.
                        </p>
                        <Link
                            to="/portfolio"
                            data-testid="home-cta-portfolio"
                            className="nav-link inline-block mt-12 font-sans-display text-xs uppercase tracking-widest-x text-aplum hover:text-aplum-accent transition-colors"
                        >
                            View Selected Works →
                        </Link>
                    </div>
                </div>
            </section>

            {/* Editorial double image */}
            <section className="px-6 md:px-12 lg:px-24 pb-32">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1 }}
                        className="md:col-span-7"
                    >
                        <img
                            src="https://images.unsplash.com/photo-1765813397347-0fa191ab77bf?crop=entropy&cs=srgb&fm=jpg&q=85"
                            alt="Studio work"
                            className="w-full h-[60vh] object-cover"
                        />
                        <p className="font-sans-display text-[11px] uppercase tracking-widest-x text-aplum-dim mt-4">
                            From the studio, autumn 2025
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="md:col-span-5 md:pb-24"
                    >
                        <h3 className="font-serif-display text-3xl md:text-4xl tracking-tighter text-aplum">
                            Current series.
                        </h3>
                        <p className="font-sans-display text-sm md:text-base text-aplum-dim mt-6 leading-relaxed">
                            <em className="italic text-aplum">The Boys Behind the Doors</em> — a charcoal series in
                            preparation for a 2026 solo exhibition. Large-scale works on paper exploring
                            interior masculinity and the architecture of distance.
                        </p>
                        <Link
                            to="/portfolio/the-boys-behind-the-doors"
                            data-testid="home-cta-series"
                            className="nav-link inline-block mt-8 font-sans-display text-xs uppercase tracking-widest-x text-aplum-accent"
                        >
                            Enter the series →
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
